/**
 * The class is a wrapper around the mapboxgl class.
 * Designed to include all the necessary settings and data required
 * for the DataPages to work. These are map settings, rendering buttons
 * for switching map types, adding layers, and more. Everything you need
 * to do with the map in this class.
 */

class DataMap {
    /**
     * Access Token for work map
     * @doc https://docs.mapbox.com/help/getting-started/access-tokens/
     * @type String
     */

    ACCESS_TOKEN =
        // eslint-disable-next-line no-secrets/no-secrets
        'pk.eyJ1IjoidHJlcmMxMiIsImEiOiJjbHN1ajJjZGswOXJ0MmtrbnhsbGV0MHVjIn0.t-pl01YclR3aaKSp1YGzBg';

    /**
     * Instance Mapbox
     * @type mapboxgl.Map
     */

    mapbox;

    /**
     * I'd for area
     * @type String
     */

    ID_AREA = 'data-page-area';

    /**
     * Class for styling
     * @type String
     */

    STYLING_CLASS = 'data-map';

    /**
     * Array of card types
     * @url https://docs.mapbox.com/api/maps/styles/#mapbox-styles
     * @param Array<Object>
     */

    MAP_STYLE_TYPES = [
        {
            type: 'satellite-streets-v12',
            label: 'satellite',
        },
        {
            type: 'light-v11',
            label: 'light',
        },
        {
            type: 'dark-v11',
            label: 'dark',
        },
        {
            type: 'streets-v12',
            label: 'streets',
        },
        {
            type: 'outdoors-v12',
            label: 'outdoors',
        },
    ];

    /**
     * ID map
     * @type String
     */

    MAP_ID = 'data-page-mapbox';

    /**
     * @param {String} selector
     */

    /**
     * Geometry for area
     * @param String
     */

    geometry;

    /**
     * ID for geolocation
     */

    geo_id;

    /**
     * Type geolocation
     */

    geo_type;

    constructor(selector) {
        this._initMarkupMap(selector);

        window.mapboxgl.accessToken = this.ACCESS_TOKEN;
        this.mapbox = new window.mapboxgl.Map({
            container: this.MAP_ID,
            style: `mapbox://styles/mapbox/${this.MAP_STYLE_TYPES[3].type}`,
            center: [-96, 33],
            zoom: 6,
            cooperativeGestures: true,
            // minZoom: 4,
        });

        this.mapbox.on('style.load', () => {
            if (this.geometry) {
                this._updateLayer();
            }
        });
    }

    resize() {
        this.mapbox.resize();
        this._fitBounds(this.geometry);
    }

    /**
     * Selecting an area on the map.
     */

    selectArea(geometry, id = '', type = '') {
        this.mapbox.resize();
        this.geometry = geometry;
        this.geo_id = id;
        this.geo_type = type;

        this.mapbox.getStyle().layers.forEach((layer) => {
            if (layer.id.includes(this.ID_AREA)) {
                this.mapbox.removeLayer(layer.id);
            }
        });

        let cutterSource = this.mapbox.getSource(this.ID_AREA);
        if (cutterSource) {
            this.mapbox.removeSource(this.ID_AREA);
        }

        this._updateLayer();
    }

    /**
     * Initializing map navigation
     */

    _initNavigations() {
        let menuElement = document.createElement('div');
        menuElement.classList.add(`${this.STYLING_CLASS}__menu`);

        let count = 0;
        this.MAP_STYLE_TYPES.forEach((map) => {
            if (count === 3) {
                menuElement.append(
                    this._generateOption(map.type, map.label, true),
                );
            } else {
                menuElement.append(this._generateOption(map.type, map.label));
            }
            count++;
        });
        return menuElement;
    }

    /**
     * Generate options for map navigation
     * @param {String} value
     * @param {String} label
     * @param {Boolean} checked
     * @return {HTMLLabelElement}
     */

    _generateOption(value, label, checked = false) {
        let labelElement = document.createElement('label');
        let spanForLabel = document.createElement('span');
        let radioElement = document.createElement('input');

        radioElement.value = value;
        radioElement.type = 'radio';
        radioElement.name = 'mapbox';
        radioElement.classList.add(`${this.STYLING_CLASS}__radio`);
        radioElement.addEventListener('change', (e) => {
            this._changeMapType(e.target.value);
        });

        if (checked) {
            radioElement.checked = true;
        }

        spanForLabel.innerText = label;
        spanForLabel.classList.add(`${this.STYLING_CLASS}__span`);

        labelElement.append(radioElement);
        labelElement.append(spanForLabel);
        labelElement.classList.add(`${this.STYLING_CLASS}__label`);

        return labelElement;
    }

    /**
     * Init markup map
     */

    _initMarkupMap(selector) {
        let container = document.querySelector(selector);
        container.classList.add(`${this.STYLING_CLASS}`);
        container.style.position = 'relative';

        let mapElement = document.createElement('div');
        mapElement.id = this.MAP_ID;
        container.classList.add(`${this.STYLING_CLASS}__map`);
        mapElement.style.width = '100%';
        mapElement.style.height = '450px';

        if (this.MAP_STYLE_TYPES) {
            container.append(this._initNavigations());
        }

        container.append(mapElement);
    }

    /**
     * Event for options click
     * @param {String} type
     */

    _changeMapType(type) {
        this.mapbox.setStyle('mapbox://styles/mapbox/' + type);
        this._updateLayer();
    }

    /**
     * Update layer
     */

    _updateLayer() {
        let currentSource = this.mapbox.getSource(this.ID_AREA);

        if (!currentSource) {
            this.mapbox.addSource(this.ID_AREA, {
                type: 'geojson',
                data: {
                    type: 'Feature',
                    geometry: this.geometry,
                },
            });
        }

        let currentLayer = this.mapbox.getLayer(this.ID_AREA);

        if (!currentLayer) {
            this.mapbox.addLayer({
                id: this.ID_AREA,
                type: 'fill',
                source: this.ID_AREA,
                layout: {},
                paint: {
                    'fill-color': '#0080ff', // blue color fill
                    'fill-opacity': 0.5,
                },
            });
        }

        this._fitBounds(this.geometry);
    }

    /**
     * Set Layout to the center and to the borders of the map
     * @param {Object<Array>} geometry
     */

    _fitBounds(geometry) {
        if (!geometry || !geometry.coordinates) {
            return;
        }
        // TODO: Coordinates are not always correct. Sometimes the array is wrong.
        let minX = Number.POSITIVE_INFINITY;
        let minY = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let maxY = Number.NEGATIVE_INFINITY;

        let coordinates = geometry.coordinates;

        if (typeof coordinates[0][0][0] == 'object') {
            let result = [];
            coordinates.forEach((el) => {
                el[0].forEach((variable) => {
                    result.push(variable);
                });
            });
            coordinates = result;
        } else {
            coordinates = coordinates[0];
        }

        for (let i = 0; i < coordinates.length; i++) {
            let coord = coordinates[i];
            let x = coord[0];
            let y = coord[1];

            minX = Math.min(minX, +x);
            minY = Math.min(minY, +y);
            maxX = Math.max(maxX, +x);
            maxY = Math.max(maxY, +y);
        }

        // The values for the USA and also for Alaska are set manually.
        // This is done due to the fact that these locations cross
        // anti-Greenwich and therefore require special handling of coordinates
        if (Number(this.geo_id) === 1 && this.geo_type === 'Country') {
            minX = -179;
            minY = 19;
            maxX = -66;
            maxY = 71;
        } else if (Number(this.geo_id) === 41 && this.geo_type === 'State') {
            minX = -179;
            minY = 51;
            maxX = -130;
            maxY = 71;
        }

        try {
            this.mapbox.fitBounds(
                [
                    [minX, minY],
                    [maxX, maxY],
                ],
                {
                    padding: 50,
                },
            );
        } catch (e) {
            throw new Error(e);
        }
    }
}

export default DataMap;
